import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import { Pill } from '../Pill';

export interface TabBarItem {
  count?: number;
  disabled?: boolean;
  label: React.ReactNode;
  value: string;
}

export interface TabBarProps {
  'aria-label': string;
  items: TabBarItem[];
  onChange: (value: string) => void;
  value: string;
}

const containerStyles = {
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 2,
  bgcolor: 'background.paper',
  overflow: 'hidden',
};
const tabsStyles = {
  minHeight: 52,
  '& .MuiTabs-indicator': { height: 2 },
  '& .MuiTab-root': {
    minHeight: 52,
    px: { xs: 2, sm: 3 },
    color: 'text.secondary',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
  },
  '& .MuiTab-root.Mui-selected': {
    color: 'text.primary',
    bgcolor: 'action.hover',
  },
};
const labelStyles = { alignItems: 'center', display: 'flex', gap: 1 };
const countStyles = { minHeight: 20, px: 0.75, fontSize: '0.75rem' };

const TabLabel = React.memo(
  ({ count, label }: Pick<TabBarItem, 'count' | 'label'>) => (
    <Box component="span" sx={labelStyles}>
      <span>{label}</span>
      {count !== undefined && <Pill sx={countStyles}>{count}</Pill>}
    </Box>
  )
);

TabLabel.displayName = 'TabLabel';

const TabBar = React.memo(
  ({ items, onChange, value, ...props }: TabBarProps) => {
    const handleChange = (_event: React.SyntheticEvent, nextValue: string) => {
      onChange(nextValue);
    };

    return (
      <Box sx={containerStyles}>
        <Tabs
          {...props}
          allowScrollButtonsMobile
          onChange={handleChange}
          scrollButtons="auto"
          sx={tabsStyles}
          value={value}
          variant="scrollable"
        >
          {items.map((item) => (
            <Tab
              disabled={item.disabled}
              key={item.value}
              label={<TabLabel count={item.count} label={item.label} />}
              value={item.value}
            />
          ))}
        </Tabs>
      </Box>
    );
  }
);

TabBar.displayName = 'TabBar';

export { TabBar };
